import { z } from "zod";
import { deliveryStatusSchema, emailSchema, firestoreDocument, norwegianBankAccountSchema, offerResponseSchema, phoneSchema, recordedResponseSchema, settlementTypeSchema, stoneHandlingDetailsSchema, stoneHandlingSchema, stoneRemovalChargeSchema, updatedResponseSchema, } from "./common.js";
const allowedAssessmentKarats = [9, 14, 18, 22, 24];
const assessmentKaratSchema = z
    .number()
    .int()
    .refine((karat) => allowedAssessmentKarats.includes(karat), "Karat må være 9, 14, 18, 22 eller 24");
const assessmentKaratRowsSchema = z
    .array(z.object({
    karat: assessmentKaratSchema,
    weightGrams: z.number().positive().max(1_000_000),
    pricePerGram: z.number().positive().max(100_000),
}))
    .min(1)
    .max(24);
export const createDeliveryAssessmentSchema = z.object({
    settlementType: settlementTypeSchema,
    rows: assessmentKaratRowsSchema,
    totalValueOverrideNok: z.number().min(0).max(100_000_000).nullable().optional(),
    payoutValueOverrideNok: z.number().min(0).max(100_000_000).nullable().optional(),
    giftCardValueOverrideNok: z.number().min(0).max(100_000_000).nullable().optional(),
    sendOffer: z.boolean().default(true),
    manifestId: z.string().min(1).max(120).nullable().optional(),
    comment: z.string().max(2000).optional(),
    expiresInDays: z.number().int().min(1).max(60).default(7),
    /**
     * True once staff have fetched today's gold prices into the form: the save then prices the rows
     * from the current price list and stores it as the delivery's snapshot. False keeps the snapshot
     * the delivery already has, so a resend never moves the price behind anyone's back.
     */
    refreshGoldPrices: z.boolean().default(false),
});
export const assessmentRowSchema = z.object({
    karat: z.number(),
    weightGrams: z.number(),
    goldPricePerGram: z.number().nullish(),
    pricePerGram: z.number(),
    offerPricePerGram: z.number().nullish(),
    valueNok: z.number(),
});
export const deliveryItemSchema = z.object({
    id: z.string(),
    description: z.string().nullable(),
    staffComment: z.string().nullish(),
    createdByUid: z.string().nullable(),
    deletedAt: z.number().nullish(),
    deletedByUid: z.string().nullish(),
    stoneRemovalCharge: stoneRemovalChargeSchema.nullish(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
/** Adding a line to a delivery: staff type the description, the server mints the rest of the item. */
export const addDeliveryItemSchema = z.object({
    description: z.string().max(500).optional(),
});
/** Editing one line. Every field is optional so a caller can touch one without clearing the others. */
export const updateDeliveryItemSchema = z
    .object({
    description: z.string().max(500).nullable(),
    staffComment: z.string().max(1000).nullable(),
    stoneRemovalCharge: stoneRemovalChargeSchema.nullable(),
})
    .partial();
const storeCheckinItemSchema = z
    .object({
    description: z.string().max(500).nullable().optional(),
    grossWeightGrams: z.number().nonnegative().max(100_000).nullable().optional(),
    stoneWeightGrams: z.number().nonnegative().max(100_000).nullable().optional(),
    netWeightGrams: z.number().nonnegative().max(100_000).nullable().optional(),
    karat: z.number().int().min(1).max(24).nullable().optional(),
})
    .refine((v) => (v.description?.trim().length ?? 0) > 0 ||
    v.grossWeightGrams != null ||
    v.netWeightGrams != null ||
    v.karat != null, "Fyll inn minst én detalj på linjen");
export const createStoreCheckinSchema = z.object({
    customerUid: z.string().min(1),
    manifestId: z.string().min(1).max(64).optional(),
    bookingId: z.string().min(1).optional(),
    notes: z.string().max(2000).optional(),
    items: z.array(storeCheckinItemSchema).max(50).default([]),
});
/**
 * The one patch applied to a delivery, whichever way the gold arrived. Weighing, archiving and
 * reopening are the same operations on both, so a mail order is updated through this rather than
 * through postal-leg "actions" that were never postal.
 */
export const updateDeliverySchema = z.object({
    notes: z.string().max(2000).nullable().optional(),
    status: deliveryStatusSchema.optional(),
    archiveReason: z.string().max(500).nullable().optional(),
    receivedWeightGrams: z.number().positive().max(1_000_000).nullable().optional(),
    /** True once staff have written the delivery into the store's paper police book; false undoes it. */
    policeBookNoted: z.boolean().optional(),
});
const kioskStoneHandlingDetailsSchema = stoneHandlingDetailsSchema;
export const kioskDeliverySchema = z.object({
    manifestId: z.string().min(1).max(64),
    vippsLinkToken: z.string().min(16).max(200).optional(),
    receivedWeightGrams: z.number().positive().max(1_000_000),
    customer: z.object({
        name: z.string().min(1).max(120),
        email: emailSchema.optional(),
        phone: phoneSchema,
        address: z.string().min(1).max(500),
        postalCode: z
            .string()
            .regex(/^\d{4}$/)
            .optional(),
        city: z.string().min(1).max(120).optional(),
        bankAccountNumber: norwegianBankAccountSchema,
        kontoEier: z.string().min(1).max(120),
    }),
    items: z
        .array(z.object({
        description: z.string().max(500).optional(),
        imagePaths: z.array(z.string().min(1).max(500)).max(8).optional(),
    }))
        .min(1)
        .max(50),
    stoneHandling: stoneHandlingSchema.optional().default("KEEP_AS_IS"),
    stoneHandlingDetails: kioskStoneHandlingDetailsSchema.nullable().optional().default(null),
    salesTermsAccepted: z.boolean().optional(),
    notes: z.string().max(2000).optional(),
});
/** The answer itself. The approval-link door takes only this — its token names the delivery. */
export const respondDeliveryOfferSchema = z.object({
    response: offerResponseSchema,
    settlementType: settlementTypeSchema.optional(),
    comment: z.string().max(1000).optional(),
});
/** The signed-in door has no token to name the delivery, so the reference travels in the body. */
export const respondDeliveryOfferWithReferenceSchema = respondDeliveryOfferSchema.extend({
    referenceNumber: z.string(),
});
export const createReturnPaymentSchema = z.object({
    amountNok: z.number().positive().max(100_000),
    note: z.string().max(500).optional(),
});
export const updateDeliveryCommentSchema = z.object({
    customerComment: z.string().max(2000).nullable().optional().default(null),
});
export const kioskDeliveryResponseSchema = z.object({
    checkinId: z.string(),
    queueId: z.string().nullable(),
    queueNumber: z.number().nullable(),
    referenceNumber: z.string(),
    itemCount: z.number(),
    receivedWeightGrams: z.number(),
    customerEmail: z.string().nullable(),
    customerPhone: z.string(),
    statusUrl: z.string(),
});
export const deliveryStatusResponseSchema = z.object({
    referenceNumber: z.string(),
    queueNumber: z.number().nullable(),
    queueStatus: z.string().nullable(),
    createdAt: z.number(),
    itemCount: z.number(),
    customerName: z.string().nullable(),
    customerComment: z.string().nullable(),
    receivedWeightGrams: z.number().nullable(),
    receivedWeightRecordedAt: z.number().nullable(),
    assessmentRows: z.array(assessmentRowSchema),
    assessmentComment: z.string().nullable(),
    assessmentSettlementType: settlementTypeSchema.nullable(),
    assessmentOfferRate: z.number().nullable(),
    assessmentGoldValue: z.number().nullable(),
    assessmentTotalValue: z.number().nullable(),
    assessmentDeductionNok: z.number().nullable(),
    assessmentPayoutValueOverrideNok: z.number().nullable(),
    assessmentGiftCardValueOverrideNok: z.number().nullable(),
    assessmentExpiresAt: z.number().nullable(),
    assessmentResponse: offerResponseSchema.nullable(),
    assessmentResponseComment: z.string().nullable(),
    assessmentRespondedAt: z.number().nullable(),
    returnPayment: z
        .object({
        status: z.string().nullable(),
        amount: z.number().nullable(),
        checkoutUrl: z.string().nullable(),
    })
        .nullable(),
    status: z.object({
        code: z.string(),
        label: z.string(),
        message: z.string(),
    }),
    items: z.array(z.object({
        id: z.string(),
        description: z.string().nullable(),
    })),
    // Present only when the request carried an approval token that held. `approvalError` says why
    // it did not, so a status page reached with a dead token can explain itself in the same read.
    approval: z
        .object({
        expiresAt: z.number(),
        usedAt: z.number().nullable(),
    })
        .nullable(),
    approvalError: z
        .enum(["BAD_TOKEN", "NOT_FOUND", "TOKEN_EXPIRED", "TOKEN_REVOKED", "MISMATCH"])
        .nullable(),
    // Where a payout would go: the delivery profile's live account, behind the same gate as the
    // figures — a token that held, or the delivery's own customer signed in. `ownProfile` is true
    // only for that signed-in owner, the one reader for whom a "change it" link edits the right profile.
    payoutAccount: z
        .object({
        bankAccountNumber: z.string().nullable(),
        ownProfile: z.boolean(),
    })
        .nullable(),
});
export const createStoreCheckinResponseSchema = z.object({
    ok: z.literal(true),
    id: z.string(),
    receiptCode: z.string(),
    itemCount: z.number(),
    queueId: z.string().nullable(),
    queueNumber: z.number().nullable(),
});
export const updateDeliveryResponseSchema = updatedResponseSchema;
export const visionPointSyncResponseSchema = z.object({ synced: z.literal(true) });
export const markDeliveryPaidResponseSchema = z.object({
    paid: z.literal(true),
    alreadyPaid: z.boolean(),
});
/** One shape for both ways gold arrives — the assessment routes run the same handler. */
export const createDeliveryAssessmentResponseSchema = z.object({
    rows: z.array(assessmentRowSchema),
    totalValue: z.number(),
    /** The offer deadline when this call sent it; null when it only saved. */
    expiresAt: z.number().nullable(),
    /**
     * What became of the offer e-mail, the customer's only link to answer with: SENT when it was
     * queued, NO_ADDRESS when the customer has no e-mail, FAILED when minting the link or queueing
     * the mail failed. Null when the call only saved.
     */
    offerEmail: z.enum(["SENT", "NO_ADDRESS", "FAILED"]).nullable(),
});
/**
 * Staff scanning or typing a reference. Unlike the customer-facing resolve, an ambiguous code is
 * handed back as a list rather than refused: staff can see the two candidates and pick deliberately.
 */
export const deliveryByCodeResponseSchema = z.object({
    delivery: firestoreDocument().optional(),
    multiple: z.literal(true).optional(),
    deliveries: z.array(firestoreDocument()).optional(),
});
export const approvalLinkResponseSchema = z.object({
    url: z.string(),
    expiresAt: z.number(),
});
export const deliveryItemResponseSchema = z.object({ item: deliveryItemSchema });
export const deletedDeliveryItemResponseSchema = z.object({ deletedItem: deliveryItemSchema });
export const returnPaymentResponseSchema = z.object({
    checkoutUrl: z.string(),
    sessionId: z.string(),
    reused: z.boolean(),
});
export const respondDeliveryOfferResponseSchema = recordedResponseSchema;
export const updateDeliveryCommentResponseSchema = z.object({
    updated: z.literal(true),
    customerComment: z.string().nullable(),
});
